/**
 * Transcription feature documentation page
 */

import {DocsContent} from '../../../components/docs/DocsContent';
import {CodeBlock} from '../../../components/docs/CodeBlock';
import {DocsActionButtons} from '../../../components/docs/DocsActionButtons';
import {Link} from 'react-router-dom';

export function Transcription() {
  return (
    <DocsContent
      title="Transcription"
      description="Real-time and post-meeting transcription with high accuracy and speaker detection."
    >
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Overview</h2>
          <p className="text-textSecondary mb-4">
            VoxaPilot provides accurate, real-time transcription for your meetings. Whether you're
            using Zoom, Google Meet, Microsoft Teams, or uploading audio files, VoxaPilot will
            transcribe everything with high accuracy.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Real-Time Transcription</h2>
          <p className="text-textSecondary mb-4">
            For live meetings, VoxaPilot can transcribe in real-time, allowing you to:
          </p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>See transcriptions appear as people speak</li>
            <li>Search through the conversation while it's happening</li>
            <li>Share live transcripts with participants</li>
            <li>Get instant notifications for keywords or topics</li>
          </ul>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Enabling Real-Time Transcription</h3>
          <CodeBlock
            code={`// Enable real-time transcription for a meeting
const meeting = await api.meetings.create({
  title: 'Team Standup',
  platform: 'zoom',
  realTimeTranscription: true,
  meetingId: '123456789',
});

// Listen for real-time updates
meeting.on('transcript-update', (segment) => {
  console.log('New transcript:', segment.text);
  console.log('Speaker:', segment.speaker);
  console.log('Timestamp:', segment.timestamp);
});`}
            language="javascript"
          />
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Post-Meeting Transcription</h2>
          <p className="text-textSecondary mb-4">
            For uploaded audio files or recorded meetings, VoxaPilot processes the entire audio
            file and generates a complete transcript. This method typically provides:
          </p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Higher accuracy (full context available)</li>
            <li>Better speaker identification</li>
            <li>More detailed timestamps</li>
            <li>Punctuation and formatting</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Speaker Detection</h2>
          <p className="text-textSecondary mb-4">
            VoxaPilot automatically identifies and labels speakers in your meetings. You can:
          </p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Assign names to speakers</li>
            <li>Merge or split speaker segments</li>
            <li>View speaker statistics and participation</li>
            <li>Filter transcripts by speaker</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Accuracy & Quality</h2>
          <p className="text-textSecondary mb-4">
            VoxaPilot achieves 99% accuracy for clear audio. To get the best results:
          </p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Use a good quality microphone</li>
            <li>Minimize background noise</li>
            <li>Speak clearly and at a moderate pace</li>
            <li>Ensure stable internet connection for real-time transcription</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Supported Formats</h2>
          <p className="text-textSecondary mb-4">VoxaPilot supports the following audio formats:</p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>MP3, MP4, WAV, M4A</li>
            <li>Video files (audio will be extracted)</li>
            <li>Live meeting platforms (Zoom, Teams, Google Meet)</li>
            <li>Direct audio streaming</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Next Steps</h2>
          <ul className="list-disc list-inside space-y-2 text-textSecondary dark:text-[#858585]">
            <li>
              <Link to="/docs/features/summaries" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Learn about AI summaries
              </Link>
            </li>
            <li>
              <Link to="/docs/integrations/zoom" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Set up Zoom integration
              </Link>
            </li>
            <li>
              <Link to="/docs/api/endpoints" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Use the transcription API
              </Link>
            </li>
          </ul>
        </section>

        <DocsActionButtons pageTitle="Transcription" />
      </div>
    </DocsContent>
  );
}

